import React, { useState, useEffect, useContext, useCallback } from 'react';
import { supabase } from 'lib/supabase';
import AdminDashboardLayout, { KPIRefreshContext } from '../admin-dashboard-overview/components/AdminDashboardLayout';
import ProfessionistiTable from './components/ProfessionistiTable';
import SearchBar from '../admin-clients-management/components/SearchBar';
import VerificationFilter from './components/VerificationFilter';
import AccountStatusFilter from './components/AccountStatusFilter';
import Pagination from '../admin-clients-management/components/Pagination';


const AdminProfessionistiManagement = () => {
  const [professionisti, setProfessionisti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [verificationFilter, setVerificationFilter] = useState('all');
  const [accountStatusFilter, setAccountStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [toast, setToast] = useState(null);
  const itemsPerPage = 25;
  const kpiRefresh = useContext(KPIRefreshContext);

  useEffect(() => {
    fetchProfessionisti(currentPage, searchQuery, verificationFilter, accountStatusFilter);
  }, [currentPage, searchQuery, verificationFilter, accountStatusFilter]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const fetchProfessionisti = async (page, search, verStatus, accStatus) => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        ?.from('users')
        ?.select(`
          *,
          quotes_sent:quotes!professional_id(count),
          quotes_accepted:quotes!professional_id(count)
        `, { count: 'exact' })
        ?.eq('role', 'professionista')
        ?.order('created_at', { ascending: false });

      // Apply search filter (email or profession)
      if (search?.trim()) {
        query = query?.or(`email.ilike.%${search?.trim()}%,profession.ilike.%${search?.trim()}%`);
      }

      // Apply verification status filter
      if (verStatus !== 'all') {
        query = query?.eq('verification_status', verStatus);
      }

      // Apply account status filter
      if (accStatus !== 'all') {
        query = query?.eq('account_status', accStatus);
      }

      // Apply pagination
      const from = (page - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;
      console.log('[fetchProfessionisti] page:', page, '| from:', from, '| to:', to);
      query = query?.range(from, to);

      const { data, error: fetchError, count } = await query;

      if (fetchError) throw fetchError;

      // Fetch quote counts separately for accuracy
      const profIds = (data || [])?.map(p => p?.id);
      let quotesMap = {};
      let quotesAcceptedMap = {};

      if (profIds?.length > 0) {
        const { data: quotesData } = await supabase
          ?.from('quotes')
          ?.select('professional_id, status')
          ?.in('professional_id', profIds);

        (quotesData || [])?.forEach(q => {
          quotesMap[q.professional_id] = (quotesMap?.[q?.professional_id] || 0) + 1;
          if (q?.status === 'accepted') {
            quotesAcceptedMap[q.professional_id] = (quotesAcceptedMap?.[q?.professional_id] || 0) + 1;
          }
        });
      }

      const enriched = (data || [])?.map(p => ({
        ...p,
        preventivi_inviati: quotesMap?.[p?.id] || 0,
        preventivi_accettati: quotesAcceptedMap?.[p?.id] || 0,
      }));

      setProfessionisti(enriched);
      setTotalCount(count || 0);
    } catch (err) {
      console.error('Error fetching professionisti:', err);
      setError('Errore nel caricamento dei professionisti');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  }, []);

  const handleVerificationFilter = useCallback((status) => {
    setVerificationFilter(status);
    setCurrentPage(1);
  }, []);

  const handleAccountStatusFilter = useCallback((status) => {
    setAccountStatusFilter(status);
    setCurrentPage(1);
  }, []);

  const handleAction = async (professionistaId, action) => {
    try {
      // Fresh auth state
      const { data: { user: adminUser } } = await supabase?.auth?.getUser();

      // Fetch current professional data BEFORE update
      const { data: currentData, error: fetchError } = await supabase
        ?.from('users')
        ?.select('email, verification_status, account_status')
        ?.eq('id', professionistaId)
        ?.single();

      if (fetchError) throw fetchError;

      let updates = {};
      let successMessage = '';
      let emailStatusType = null;
      let emailNewStatus = null;
      let auditAction = '';

      if (action === 'approve') {
        updates.verification_status = 'approved';
        successMessage = 'Professionista approvato con successo';
        emailStatusType = 'verification_status';
        emailNewStatus = 'approved';
        auditAction = 'approve_professional';
      } else if (action === 'reject') {
        updates.account_status = 'rejected';
        successMessage = 'Professionista rifiutato con successo';
        emailStatusType = 'verification_status';
        emailNewStatus = 'rejected';
        auditAction = 'reject_professional';
      } else if (action === 'suspend') {
        updates.account_status = 'suspended';
        successMessage = 'Account sospeso con successo';
        emailStatusType = 'account_status';
        emailNewStatus = 'suspended';
        auditAction = 'suspend_professional';
      } else if (action === 'ban') {
        updates.account_status = 'banned';
        successMessage = 'Account bannato con successo';
        emailStatusType = 'account_status';
        emailNewStatus = 'banned';
        auditAction = 'ban_professional';
      } else if (action === 'reactivate') {
        updates.account_status = 'active';
        successMessage = 'Account riattivato con successo';
        emailStatusType = 'account_status';
        emailNewStatus = 'active';
        auditAction = 'reactivate_professional';
      }

      const { error: updateError } = await supabase
        ?.from('users')
        ?.update(updates)
        ?.eq('id', professionistaId);

      if (updateError) throw updateError;

      // Log via logAdminAction edge function
      try {
        await supabase?.functions?.invoke('logAdminAction', {
          body: {
            action: auditAction,
            entity_type: 'professional',
            entity_id: professionistaId,
            old_value: { account_status: currentData?.account_status, verification_status: currentData?.verification_status },
            new_value: updates,
          }
        });
      } catch (logErr) {
        console.error('Audit log error:', logErr);
      }

      // Detect if status actually changed for email
      const emailPreviousStatus = emailStatusType === 'verification_status'
        ? currentData?.verification_status
        : currentData?.account_status;
      const statusChanged = emailPreviousStatus !== emailNewStatus;

      if (statusChanged && emailNewStatus) {
        try {
          await supabase?.functions?.invoke('send-professional-status-email', {
            body: {
              email: currentData?.email,
              name: 'Professionista',
              statusType: emailStatusType,
              newStatus: emailNewStatus
            }
          });
        } catch (emailErr) {
          console.error('Email error:', emailErr);
        }
      }

      setToast({ type: 'success', message: successMessage });
      await fetchProfessionisti(currentPage, searchQuery, verificationFilter, accountStatusFilter);
      if (kpiRefresh?.refreshKPIs) {
        await kpiRefresh?.refreshKPIs();
      }
    } catch (err) {
      console.error('Error in handleAction:', err);
      setToast({ type: 'error', message: 'Errore durante l\'aggiornamento' });
    }
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return (
    <AdminDashboardLayout>
      <div className="space-y-6">
        {/* Toast Notification */}
        {toast && (
          <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 animate-slide-in ${
            toast?.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}>
            <span>{toast?.message}</span>
          </div>
        )}
        {/* Page Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestione Professionisti</h1>
          <p className="text-gray-600 mt-2">Visualizza e gestisci tutti i professionisti registrati</p>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SearchBar onSearch={handleSearch} placeholder="Cerca per email o professione..." />
            <VerificationFilter onFilter={handleVerificationFilter} currentFilter={verificationFilter} />
            <AccountStatusFilter onFilter={handleAccountStatusFilter} currentFilter={accountStatusFilter} />
          </div>
        </div>

        {/* Results Summary */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Totale: <span className="font-semibold">{totalCount}</span> professionisti
          </p>
          <p className="text-sm text-gray-600">
            Pagina {currentPage} di {totalPages || 1}
          </p>
        </div>

        {/* Table */}
        {loading ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-600">Caricamento...</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        ) : (
          <ProfessionistiTable
            professionisti={professionisti}
            onAction={handleAction}
            onDeleteSuccess={(deletedId) => setProfessionisti(prev => prev?.filter(p => p?.id !== deletedId))}
          />
        )}

        {/* Pagination */}
        {!loading && !error && totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => {
              console.log('[Pagination] onPageChange called with page:', page);
              setCurrentPage(page);
            }}
          />
        )}
      </div>
    </AdminDashboardLayout>
  );
};

export default AdminProfessionistiManagement;